import type { ComponentPublicInstance } from "vue";
import { createApp } from "vue";
import App from "./app/App.vue";
import router from "./router";
import { store, key } from "./store";
import { use } from "echarts/core";
import highlightDirective from "./directives/highlightDirective";
import ECharts from "vue-echarts";
import * as flags from "@/flags";
import { trace, SpanStatusCode } from "@opentelemetry/api";
import type { Exception } from "@opentelemetry/api";
import VueOpenTelemetryPlugin from "./plugins/VueOpenTelemetryPlugin";

flags.resetAllFlags();
flags.captureFlags();

// import ECharts modules manually to reduce bundle size
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart, GaugeChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  MarkLineComponent,
  ToolboxComponent,
  BrushComponent,
  LegendComponent,
} from "echarts/components";
import naive from "naive-ui";

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  GaugeChart,
  BrushComponent,
  MarkLineComponent,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  ToolboxComponent,
  LegendComponent,
]);

const app = createApp(App);

app.config.errorHandler = function (
  error: unknown,
  instance: ComponentPublicInstance | null,
  info: string
) {
  const span =
    trace.getActiveSpan() || trace.getTracer("web").startSpan("Error");
  span.setStatus({ code: SpanStatusCode.ERROR });
  span.recordException(error as Exception);
  span.end();
};

window.onerror = function (errMsg, url, line, column, error) {
  const span =
    trace.getActiveSpan() || trace.getTracer("web").startSpan("Error");
  span.setStatus({ code: SpanStatusCode.ERROR });
  span.recordException(error as Exception);
  span.end();
  return true;
};

app
  .use(store, key)
  .use(router)
  .use(naive)
  .component("v-chart", ECharts)
  .directive("highlight", highlightDirective)
  .use(VueOpenTelemetryPlugin)
  .mount("#AppMount");
